/**
 * 기본 수수료율 목록 — 레거시 `baseFeeRate.js` 포팅.
 * 정산월·설정 여부 · 검색/유형 필터 · 처방/정산 금액·수수료율 컬럼 · 월별 히스토리 모달.
 */
import type { BasicFeeHistoryItem, BasicFeeType } from '@/entities/fee-rate/api/fee-rate.api';
import {
  useBasicFeeHistory,
  useBasicFeePolicy,
  useBasicFeeUserHistory,
} from '@/entities/fee-rate/hooks/useFeeRates';
import {
  formatAmount,
  formatFeeDateTime,
  formatRate,
  formatRefYmDot,
  getBasicFeePolicyConfiguredAt,
  getCurrentRefYm,
} from '@/pages/fee-rates/feeRateUtils';
import { DataCard } from '@/shared/components/DataCard';
import { MonthPicker } from '@/shared/components/DatePicker';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/shared/components/ui/radio-group';
import { FilterRow, Stack } from '@/shared/components/ui/row';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { baseFeeRateSettingPath } from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { cn } from '@/shared/lib/utils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate } from '@tanstack/react-router';
import { Banknote, Search, Settings } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

const BASE_FEE_RATE_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 50,
  settlementYearMonth: getCurrentRefYm(),
  searchKeyword: '',
  basicFeeType: '',
};

type HistoryUser = Pick<
  BasicFeeHistoryItem,
  'userId' | 'email' | 'companyName' | 'ceoName'
>;

/** 구간/고정 구분 태그 — RANGE=primary, FIXED=warning */
const FeeTypeTag: React.FC<{ feeType?: string }> = ({ feeType }) => {
  if (feeType === 'RANGE') {
    return <Badge variant="info">구간</Badge>;
  }
  if (feeType === 'FIXED') {
    return <Badge variant="warning">고정</Badge>;
  }
  return <Badge variant="secondary">-</Badge>;
};

export const BaseFeeRatesPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { values, setValues } = useListSearchParams(
    BASE_FEE_RATE_SEARCH_DEFAULTS,
  );

  const [keywordInput, setKeywordInput] = useState(values.searchKeyword);
  const [historyUser, setHistoryUser] = useState<HistoryUser | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);

  useEffect(() => {
    setKeywordInput(values.searchKeyword);
  }, [values.searchKeyword]);

  const search = useMemo(
    () => ({
      settlementYearMonth: values.settlementYearMonth,
      searchKeyword: values.searchKeyword || undefined,
      basicFeeType: (values.basicFeeType || undefined) as
        | BasicFeeType
        | undefined,
    }),
    [values],
  );

  const pageable = useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const historyPageable = useMemo(
    () => ({ page: Math.max(0, historyPage - 1), size: historyPageSize }),
    [historyPage, historyPageSize],
  );

  const { data, isLoading, isError, error } = useBasicFeeHistory(
    search,
    pageable,
  );
  const { data: policy } = useBasicFeePolicy(search.settlementYearMonth);
  const {
    data: historyData,
    isLoading: historyLoading,
    isError: historyError,
    error: historyErr,
  } = useBasicFeeUserHistory(historyUser?.userId ?? 0, historyPageable);

  useEffect(() => {
    if (isError && error) handleApiError(error, toast);
  }, [isError, error, toast]);

  useEffect(() => {
    if (historyError && historyErr) handleApiError(historyErr, toast);
  }, [historyError, historyErr, toast]);

  const rows = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;
  const historyRows = historyData?.content ?? [];
  const historyTotal = historyData?.totalCount ?? 0;

  const configuredAt = getBasicFeePolicyConfiguredAt(policy);
  const isFeeConfigured = Boolean(policy);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      searchKeyword: keywordInput.trim(),
      page: 1,
    });
  };

  const openHistory = (row: BasicFeeHistoryItem) => {
    setHistoryPage(1);
    setHistoryPageSize(10);
    setHistoryUser({
      userId: row.userId,
      email: row.email,
      companyName: row.companyName,
      ceoName: row.ceoName,
    });
  };

  const closeHistory = () => {
    setHistoryUser(null);
    setHistoryPage(1);
  };

  const goToSetting = () => {
    navigate({
      to: baseFeeRateSettingPath(),
      search: { settlementYearMonth: values.settlementYearMonth },
    } as never);
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Banknote}
        title="기본 수수료 설정"
        actions={
          <MenuWriteGate>
            <Button type="button" className="ruda-button" onClick={goToSetting}>
              <Settings className="mr-2 h-4 w-4" />
              기본 수수료율 설정
            </Button>
          </MenuWriteGate>
        }
      />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <Field label="정산 년월" htmlFor="base-fee-ym">
            <MonthPicker
              id="base-fee-ym"
              value={values.settlementYearMonth}
              onChange={(next) =>
                setValues({
                  settlementYearMonth: next || getCurrentRefYm(),
                  page: 1,
                })
              }
              className="w-44"
            />
          </Field>
          <p
            className={cn(
              'pb-2 text-sm',
              isFeeConfigured ? 'text-ruda-success' : 'text-ruda-error',
            )}
          >
            {isFeeConfigured
              ? `수수료율이 설정되었습니다(${formatFeeDateTime(configuredAt)})`
              : '수수료율이 설정되지 않았습니다.'}
          </p>
        </CardContent>
      </Card>

      <FilterCard>
        <Stack as="form" gap={16} onSubmit={handleSearch}>
          <FilterRow as="div" gap={12}>
            <Field
              label="검색"
              htmlFor="base-fee-keyword"
              className="min-w-0 flex-1"
            >
              <Input
                id="base-fee-keyword"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                placeholder="업체명 또는 사용자 이메일"
                className="max-w-md"
              />
            </Field>
            <Button type="submit" className="ruda-button">
              <Search className="mr-2 h-4 w-4" />
              검색
            </Button>
          </FilterRow>
          <Field label="수수료 유형">
            <RadioGroup
              value={values.basicFeeType || 'all'}
              onValueChange={(value) =>
                setValues({
                  basicFeeType: value === 'all' ? '' : value,
                  page: 1,
                })
              }
              className="flex flex-wrap gap-4"
            >
              {(
                [
                  { value: 'all', label: '전체' },
                  { value: 'RANGE', label: '구간 수수료' },
                  { value: 'FIXED', label: '고정 수수료' },
                ] as const
              ).map((opt) => (
                <label
                  key={opt.value}
                  htmlFor={`base-fee-type-${opt.value}`}
                  className="flex cursor-pointer items-center gap-2 text-sm"
                >
                  <RadioGroupItem
                    value={opt.value}
                    id={`base-fee-type-${opt.value}`}
                  />
                  {opt.label}
                </label>
              ))}
            </RadioGroup>
          </Field>
        </Stack>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <div className="py-16">
            <LoadingSpinner tip="기본 수수료율을 불러오는 중..." />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState description="조회된 기본 수수료율이 없습니다." />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table className="min-w-max">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-center">사용자 이메일</TableHead>
                    <TableHead className="text-center">업체명</TableHead>
                    <TableHead className="text-center">대표자</TableHead>
                    <TableHead className="text-center">구간/고정</TableHead>
                    <TableHead className="text-center">
                      처방 - 정산대상금액
                    </TableHead>
                    <TableHead className="text-center">처방 - 수수료율</TableHead>
                    <TableHead className="text-center">
                      정산 - 정산대상금액
                    </TableHead>
                    <TableHead className="text-center">정산 - 수수료율</TableHead>
                    <TableHead className="text-center">월 별 보기</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.userId}>
                      <TableCell className="text-center">{row.email}</TableCell>
                      <TableCell className="text-center">
                        {row.companyName}
                      </TableCell>
                      <TableCell className="text-center">{row.ceoName}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center">
                          <FeeTypeTag feeType={row.basicFeeType} />
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {formatAmount(row.prescriptionTargetAmount)}
                      </TableCell>
                      <TableCell className="text-center">
                        {formatRate(row.prescriptionFeeRate)}
                      </TableCell>
                      <TableCell className="text-center">
                        {formatAmount(row.settlementTargetAmount)}
                      </TableCell>
                      <TableCell className="text-center">
                        {formatRate(row.settlementFeeRate)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => openHistory(row)}
                        >
                          보기
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>

      <Dialog
        open={Boolean(historyUser)}
        onOpenChange={(open) => {
          if (!open) closeHistory();
        }}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>월 별 기본 수수료율</DialogTitle>
          </DialogHeader>
          {historyUser ? (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="사용자 이메일">
                  <Input readOnly value={historyUser.email || '-'} />
                </Field>
                <Field label="업체명">
                  <Input readOnly value={historyUser.companyName || '-'} />
                </Field>
                <Field label="대표자명">
                  <Input readOnly value={historyUser.ceoName || '-'} />
                </Field>
              </div>
              {historyLoading ? (
                <LoadingSpinner tip="월별 이력을 불러오는 중..." />
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table className="min-w-max">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-center">정산 년월</TableHead>
                          <TableHead className="text-center">구간/고정</TableHead>
                          <TableHead className="text-center">
                            처방 - 정산대상금액
                          </TableHead>
                          <TableHead className="text-center">
                            처방 - 수수료율
                          </TableHead>
                          <TableHead className="text-center">
                            정산 - 정산대상금액
                          </TableHead>
                          <TableHead className="text-center">
                            정산 - 수수료율
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {historyRows.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              className="py-8 text-center text-muted-foreground"
                            >
                              이력이 없습니다.
                            </TableCell>
                          </TableRow>
                        ) : (
                          historyRows.map((row, index) => (
                            <TableRow
                              key={`${historyUser.userId}-${row.policyId ?? index}-${row.settlementYearMonth}`}
                            >
                              <TableCell className="text-center">
                                {formatRefYmDot(row.settlementYearMonth)}
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex justify-center">
                                  <FeeTypeTag feeType={row.basicFeeType} />
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                {formatAmount(row.prescriptionTargetAmount)}
                              </TableCell>
                              <TableCell className="text-center">
                                {formatRate(row.prescriptionFeeRate)}
                              </TableCell>
                              <TableCell className="text-center">
                                {formatAmount(row.settlementTargetAmount)}
                              </TableCell>
                              <TableCell className="text-center">
                                {formatRate(row.settlementFeeRate)}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <SimplePagination
                    page={historyPage}
                    pageSize={historyPageSize}
                    total={historyTotal}
                    onPageChange={setHistoryPage}
                    onPageSizeChange={(size) => {
                      setHistoryPageSize(size);
                      setHistoryPage(1);
                    }}
                  />
                </>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeHistory}>
              닫기
            </Button>
            <MenuWriteGate>
              <Button
                type="button"
                className="ruda-button"
                onClick={() => {
                  closeHistory();
                  goToSetting();
                }}
              >
                설정 화면 이동
              </Button>
            </MenuWriteGate>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
